import Fuse from "fuse.js";
import {
  normalizeProductNumber,
  getPartNumberLookupShard,
  getProductIdShard,
  normalizeSearchTitle,
  getTitleSearchShard,
} from "../catalog/normalizeProductNumber";

const shardCache = new Map();
const CATEGORIES_ROOT_MANIFEST = "/catalog/categories/manifest.json";
const BRANDS_ROOT_MANIFEST = "/catalog/brands/manifest.json";
const SEARCH_ROOT_MANIFEST = "/catalog/search/manifest.json";
const SEARCH_MINIMUM_QUERY_LENGTH = 2;

async function fetchJsonShard(url) {
  if (shardCache.has(url)) {
    return shardCache.get(url);
  }

  const response = await fetch(url);
  if (!response.ok) {
    throw new Error(
      `Failed to load catalogue shard (${response.status}): ${url}`,
    );
  }

  const data = await response.json();
  shardCache.set(url, data);
  return data;
}

/**
 * Fetches an optional search shard as an array of records.
 * A missing prefix shard is normal (no candidates of that type) — on static
 * SPA hosting a missing file returns the index.html fallback, so we guard on
 * content-type and shape and return [] rather than throwing. Result (incl. [])
 * is cached so repeated prefixes never refetch.
 */
async function fetchSearchShardRecords(url) {
  if (shardCache.has(url)) {
    return shardCache.get(url);
  }

  let records = [];
  try {
    const response = await fetch(url);
    if (response.ok) {
      const contentType = response.headers.get("content-type") || "";
      if (contentType.includes("json")) {
        const data = await response.json();
        if (Array.isArray(data)) {
          records = data;
        }
      }
    }
  } catch {
    records = [];
  }

  shardCache.set(url, records);
  return records;
}

function isValidProductId(productId) {
  if (!productId || typeof productId !== "string") return false;
  const hex = productId.replace(/-/g, "").toLowerCase();
  return /^[0-9a-f]{32}$/.test(hex) || /^[0-9a-f-]{8,}$/i.test(productId);
}

function normalizeCategoryId(categoryId) {
  if (categoryId == null) return "";
  return String(categoryId).trim();
}

function isValidCategoryId(categoryId) {
  const id = normalizeCategoryId(categoryId);
  return /^[a-zA-Z0-9_-]+$/.test(id);
}

function decodeRouteValue(value) {
  if (value == null) return "";
  let decoded = String(value).trim();
  if (!decoded) return "";

  // Routes may arrive already decoded or still percent-encoded.
  try {
    const once = decodeURIComponent(decoded);
    if (once.includes("%")) {
      try {
        return decodeURIComponent(once).trim();
      } catch {
        return once.trim();
      }
    }
    return once.trim();
  } catch {
    return decoded;
  }
}

function toBrandSlugCandidate(value) {
  return (
    String(value || "")
      .toLowerCase()
      .replace(/&/g, " ")
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/-+/g, "-")
      .replace(/^-+|-+$/g, "") || ""
  );
}

/**
 * @param {string} productId
 * @returns {Promise<object|null>}
 */
export async function getProductById(productId) {
  const id = productId == null ? "" : String(productId).trim();
  if (!id || !isValidProductId(id)) {
    return null;
  }

  const shard = getProductIdShard(id);
  const url = `/catalog/products/by-id/${shard}.json`;
  const shardData = await fetchJsonShard(url);
  return shardData[id] || null;
}

/**
 * @param {string} productNumber
 * @returns {Promise<{product: object|null, matchingIds: string[], hasDuplicates: boolean}|null>}
 */
export async function getProductByPartNumber(productNumber) {
  const normalized = normalizeProductNumber(productNumber);
  if (!normalized) {
    return null;
  }

  const shard = getPartNumberLookupShard(normalized);
  const url = `/catalog/products/by-pn/${shard}.json`;
  const lookup = await fetchJsonShard(url);
  const matchingIds = lookup[normalized];

  if (!matchingIds || !Array.isArray(matchingIds) || matchingIds.length === 0) {
    return null;
  }

  const chosenId = matchingIds[0];
  const product = await getProductById(chosenId);

  if (!product) {
    return null;
  }

  return {
    product,
    matchingIds,
    hasDuplicates: matchingIds.length > 1,
  };
}

/**
 * @returns {Promise<object>}
 */
export async function getCategoriesManifest() {
  return fetchJsonShard(CATEGORIES_ROOT_MANIFEST);
}

/**
 * Resolves a category entry from the root categories manifest.
 * Matches by exact id or slug (case-insensitive for convenience).
 * @param {string} categoryId
 * @returns {Promise<object|null>}
 */
export async function getCategoryManifest(categoryId) {
  const id = normalizeCategoryId(categoryId);
  if (!id || !isValidCategoryId(id)) {
    return null;
  }

  const root = await getCategoriesManifest();
  const categories = Array.isArray(root?.categories) ? root.categories : [];
  const needle = id.toLowerCase();
  const summary = categories.find(
    (category) =>
      String(category.id || "").toLowerCase() === needle ||
      String(category.slug || "").toLowerCase() === needle,
  );

  if (!summary?.manifestPath) {
    return null;
  }

  const detail = await fetchJsonShard(summary.manifestPath);
  return {
    ...summary,
    ...detail,
  };
}

/**
 * Loads one category product page.
 * @param {string} categoryId
 * @param {number} page
 * @returns {Promise<{
 *   category: object,
 *   products: object[],
 *   page: number,
 *   pageCount: number,
 *   productCount: number,
 *   pageSize: number,
 *   hasPreviousPage: boolean,
 *   hasNextPage: boolean
 * }|null>}
 */
export async function getCategoryProducts(categoryId, page = 1) {
  const category = await getCategoryManifest(categoryId);
  if (!category) {
    return null;
  }

  const pageCount = Number(category.pageCount) || 0;
  const requestedPage = Number(page);

  if (
    !Number.isInteger(requestedPage) ||
    requestedPage < 1 ||
    (pageCount > 0 && requestedPage > pageCount)
  ) {
    throw new Error(
      `Invalid category page number: ${page} (valid range 1-${pageCount || 0})`,
    );
  }

  if (pageCount === 0) {
    return {
      category,
      products: [],
      page: 1,
      pageCount: 0,
      productCount: Number(category.productCount) || 0,
      pageSize: Number(category.pageSize) || 0,
      hasPreviousPage: false,
      hasNextPage: false,
    };
  }

  const pageEntry = Array.isArray(category.pages)
    ? category.pages.find((entry) => Number(entry.page) === requestedPage)
    : null;

  if (!pageEntry?.path) {
    throw new Error(
      `Category page ${requestedPage} is missing from the category manifest.`,
    );
  }

  const products = await fetchJsonShard(pageEntry.path);
  if (!Array.isArray(products)) {
    throw new Error(
      `Malformed category page response for ${category.id} page ${requestedPage}.`,
    );
  }

  return {
    category,
    products,
    page: requestedPage,
    pageCount,
    productCount: Number(category.productCount) || 0,
    pageSize: Number(category.pageSize) || products.length,
    hasPreviousPage: requestedPage > 1,
    hasNextPage: requestedPage < pageCount,
  };
}

/**
 * @returns {Promise<object>}
 */
export async function getBrandsManifest() {
  return fetchJsonShard(BRANDS_ROOT_MANIFEST);
}

/**
 * Resolves a brand route value to a brand summary from the root manifest.
 * Accepts original names, decoded URL values, case variants, and slugs.
 * @param {string} routeBrandValue
 * @returns {Promise<object|null>}
 */
export async function resolveBrand(routeBrandValue) {
  const decoded = decodeRouteValue(routeBrandValue);
  if (!decoded) return null;

  const root = await getBrandsManifest();
  const brands = Array.isArray(root?.brands) ? root.brands : [];
  if (!brands.length) return null;

  const lookup =
    root?.lookup && typeof root.lookup === "object" ? root.lookup : {};
  const lower = decoded.toLowerCase();
  const slugCandidate = toBrandSlugCandidate(decoded);

  let slug =
    lookup[lower] ||
    lookup[decoded] ||
    lookup[slugCandidate] ||
    (slugCandidate ? lookup[slugCandidate] : null);

  if (!slug) {
    const exact = brands.find(
      (brand) =>
        String(brand.name || "").toLowerCase() === lower ||
        String(brand.slug || "").toLowerCase() === lower ||
        String(brand.slug || "") === slugCandidate,
    );
    slug = exact?.slug || null;
  }

  if (!slug) return null;

  const summary = brands.find(
    (brand) =>
      String(brand.slug || "").toLowerCase() === String(slug).toLowerCase(),
  );

  return summary || null;
}

/**
 * @param {string} routeBrandValue
 * @returns {Promise<object|null>}
 */
export async function getBrandManifest(routeBrandValue) {
  const summary = await resolveBrand(routeBrandValue);
  if (!summary?.manifestPath) {
    return null;
  }

  const detail = await fetchJsonShard(summary.manifestPath);
  return {
    ...summary,
    ...detail,
  };
}

/**
 * Loads one brand product page.
 * @param {string} routeBrandValue
 * @param {number} page
 * @returns {Promise<{
 *   brand: object,
 *   products: object[],
 *   page: number,
 *   pageCount: number,
 *   productCount: number,
 *   pageSize: number,
 *   hasPreviousPage: boolean,
 *   hasNextPage: boolean
 * }|null>}
 */
export async function getBrandProducts(routeBrandValue, page = 1) {
  const brand = await getBrandManifest(routeBrandValue);
  if (!brand) {
    return null;
  }

  const pageCount = Number(brand.pageCount) || 0;
  const requestedPage = Number(page);

  if (
    !Number.isInteger(requestedPage) ||
    requestedPage < 1 ||
    (pageCount > 0 && requestedPage > pageCount)
  ) {
    throw new Error(
      `Invalid brand page number: ${page} (valid range 1-${pageCount || 0})`,
    );
  }

  if (pageCount === 0) {
    return {
      brand,
      products: [],
      page: 1,
      pageCount: 0,
      productCount: Number(brand.productCount) || 0,
      pageSize: Number(brand.pageSize) || 0,
      hasPreviousPage: false,
      hasNextPage: false,
    };
  }

  const pageEntry = Array.isArray(brand.pages)
    ? brand.pages.find((entry) => Number(entry.page) === requestedPage)
    : null;

  if (!pageEntry?.path) {
    throw new Error(
      `Brand page ${requestedPage} is missing from the brand manifest.`,
    );
  }

  const products = await fetchJsonShard(pageEntry.path);
  if (!Array.isArray(products)) {
    throw new Error(
      `Malformed brand page response for ${brand.name} page ${requestedPage}.`,
    );
  }

  return {
    brand,
    products,
    page: requestedPage,
    pageCount,
    productCount: Number(brand.productCount) || 0,
    pageSize: Number(brand.pageSize) || products.length,
    hasPreviousPage: requestedPage > 1,
    hasNextPage: requestedPage < pageCount,
  };
}

/**
 * @returns {Promise<object>}
 */
export async function getSearchManifest() {
  return fetchJsonShard(SEARCH_ROOT_MANIFEST);
}

function rankSearchRecord(record, normalizedPn, normalizedTitle) {
  const recPn = record.normalizedProductNumber || "";
  const recTitle = record.normalizedTitle || "";
  const hasPnQuery = normalizedPn.length >= SEARCH_MINIMUM_QUERY_LENGTH;
  const hasTitleQuery = normalizedTitle.length >= SEARCH_MINIMUM_QUERY_LENGTH;

  if (hasPnQuery && recPn === normalizedPn) return 1;
  if (hasPnQuery && recPn.startsWith(normalizedPn)) return 2;
  if (hasTitleQuery && recTitle === normalizedTitle) return 3;
  if (hasTitleQuery && recTitle.startsWith(normalizedTitle)) return 4;
  if (hasPnQuery && recPn.includes(normalizedPn)) return 5;
  if (hasTitleQuery && recTitle.includes(normalizedTitle)) return 6;
  return Infinity;
}

/**
 * Searches lightweight search shards for a query.
 * Only the relevant part-number and title prefix shards are fetched.
 *
 * @param {string} query
 * @param {{limit?: number, includePartNumberSearch?: boolean, includeTitleSearch?: boolean}} [options]
 * @returns {Promise<{query: string, results: object[], loadedShards: string[], totalCandidates: number}>}
 */
export async function searchProducts(query, options = {}) {
  const {
    limit = 15,
    includePartNumberSearch = true,
    includeTitleSearch = true,
  } = options;

  const rawQuery = query == null ? "" : String(query).trim();
  const normalizedPn = normalizeProductNumber(rawQuery);
  const normalizedTitle = normalizeSearchTitle(rawQuery);

  const empty = {
    query: rawQuery,
    results: [],
    loadedShards: [],
    totalCandidates: 0,
  };

  const hasPnQuery = normalizedPn.length >= SEARCH_MINIMUM_QUERY_LENGTH;
  const hasTitleQuery = normalizedTitle.length >= SEARCH_MINIMUM_QUERY_LENGTH;
  if (!hasPnQuery && !hasTitleQuery) {
    return empty;
  }

  const shardUrls = new Set();
  if (includePartNumberSearch && hasPnQuery) {
    const pnShard = getPartNumberLookupShard(normalizedPn);
    shardUrls.add(`/catalog/search/pn/${pnShard}.json`);
  }
  if (includeTitleSearch && hasTitleQuery) {
    const titleShard = getTitleSearchShard(normalizedTitle);
    shardUrls.add(`/catalog/search/title/${titleShard}.json`);
  }

  if (!shardUrls.size) {
    return empty;
  }

  const loadedShards = [];
  const candidatesById = new Map();

  await Promise.all(
    [...shardUrls].map(async (url) => {
      const records = await fetchSearchShardRecords(url);
      loadedShards.push(url);
      for (const record of records) {
        if (record && record.id && !candidatesById.has(record.id)) {
          candidatesById.set(record.id, record);
        }
      }
    }),
  );

  const candidates = [...candidatesById.values()];
  const totalCandidates = candidates.length;
  if (!totalCandidates) {
    return { query: rawQuery, results: [], loadedShards, totalCandidates };
  }

  const ranked = candidates
    .map((record) => ({
      record,
      rank: rankSearchRecord(record, normalizedPn, normalizedTitle),
    }))
    .filter((entry) => entry.rank !== Infinity);

  // Fuzzy fallback (tier 7) only over the small loaded candidate set.
  if (ranked.length < limit) {
    const matchedIds = new Set(ranked.map((entry) => entry.record.id));
    const remaining = candidates.filter((record) => !matchedIds.has(record.id));
    if (remaining.length) {
      const fuse = new Fuse(remaining, {
        keys: ["productTitle", "productNumber"],
        threshold: 0.4,
        includeScore: true,
      });
      const fuzzy = fuse.search(rawQuery);
      for (const hit of fuzzy) {
        ranked.push({ record: hit.item, rank: 7, score: hit.score || 0 });
      }
    }
  }

  ranked.sort((a, b) => {
    if (a.rank !== b.rank) return a.rank - b.rank;
    if ((a.score || 0) !== (b.score || 0)) {
      return (a.score || 0) - (b.score || 0);
    }
    const pnA = a.record.normalizedProductNumber || "";
    const pnB = b.record.normalizedProductNumber || "";
    if (pnA !== pnB) return pnA.localeCompare(pnB);
    return (a.record.id || "").localeCompare(b.record.id || "");
  });

  const results = ranked.slice(0, limit).map((entry) => entry.record);
  return { query: rawQuery, results, loadedShards, totalCandidates };
}

export {
  normalizeProductNumber,
  getPartNumberLookupShard,
  getProductIdShard,
  normalizeSearchTitle,
  getTitleSearchShard,
};

export function clearCatalogShardCache() {
  shardCache.clear();
}
